import React, { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useAuth } from '../contexts/AuthContext';
import { AdminViewBadge } from '../components/AdminOverlay';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import { Button } from '../components/ui/button';
import { Sheet, SheetContent, SheetTrigger } from '../components/ui/sheet';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import {
  LayoutDashboard, PenSquare, CalendarDays, Activity,
  Settings, CreditCard, Shield, LogOut, Menu, Coins, ChevronLeft, ChevronRight, Brain
} from 'lucide-react';

const LANGUAGES = [
  { code: 'tr', label: 'Türkçe', flag: '🇹🇷' },
  { code: 'en', label: 'English', flag: '🇺🇸' },
  { code: 'de', label: 'Deutsch', flag: '🇩🇪' },
  { code: 'fr', label: 'Français', flag: '🇫🇷' },
  { code: 'es', label: 'Español', flag: '🇪🇸' },
  { code: 'ar', label: 'العربية', flag: '🇸🇦' },
];

export function AppShell({ children }) {
  const { t, i18n } = useTranslation();
  const { user, logout } = useAuth();
  const location = useLocation();
  const navigate = useNavigate();
  const [collapsed, setCollapsed] = useState(false);
  const [mobileOpen, setMobileOpen] = useState(false);

  const navItems = [
    { path: '/dashboard', icon: LayoutDashboard, label: t('nav.dashboard') },
    { path: '/studio', icon: PenSquare, label: 'Content Studio', highlight: true },
    { path: '/ai-studio', icon: Brain, label: t('nav.aiStudio') },
    { path: '/calendar', icon: CalendarDays, label: t('nav.calendar') },
    { path: '/activity', icon: Activity, label: t('nav.activity') },
    { path: '/settings', icon: Settings, label: t('nav.settings') },
    { path: '/billing', icon: CreditCard, label: t('nav.billing') },
  ];

  if (user?.role === 'admin') {
    navItems.push({ path: '/admin', icon: Shield, label: t('nav.admin'), admin: true });
  }

  const handleLogout = () => {
    logout();
    navigate('/');
  };

  const NavContent = () => (
    <div className="flex flex-col h-full">
      {/* Logo */}
      <div className="flex items-center gap-2 px-4 py-5 border-b border-white/10">
        <div className="w-8 h-8 rounded-lg bg-gradient-to-br from-[hsl(46,79%,56%)] to-[hsl(174,72%,38%)] flex items-center justify-center font-bold text-black text-sm">
          T
        </div>
        {!collapsed && <span className="text-lg font-semibold tracking-tight" style={{ fontFamily: 'Space Grotesk, sans-serif' }}>TekCep</span>}
      </div>

      {/* Nav Items */}
      <nav className="flex-1 py-4 px-2 space-y-1">
        {navItems.map(item => {
          const isActive = location.pathname === item.path;
          return (
            <Link
              key={item.path}
              to={item.path}
              onClick={() => setMobileOpen(false)}
              data-testid={`nav-${item.path.replace('/', '')}`}
              className={`flex items-center gap-3 px-3 py-2.5 rounded-lg text-sm transition-colors duration-200 ${
                isActive
                  ? 'sidebar-active text-[hsl(var(--foreground))] font-medium'
                  : 'text-[hsl(var(--muted-foreground))] hover:text-[hsl(var(--foreground))] hover:bg-white/5'
              }`}
            >
              <item.icon size={18} className={item.highlight && !isActive ? 'text-[hsl(var(--primary))]' : ''} />
              {!collapsed && <span>{item.label}</span>}
              {!collapsed && item.highlight && (
                <span className="ml-auto text-[10px] px-1.5 py-0.5 rounded bg-[hsl(var(--primary))]/20 text-[hsl(var(--primary))] font-medium">AI</span>
              )}
              {!collapsed && item.admin && (
                <span className="ml-auto text-[10px] px-1.5 py-0.5 rounded bg-[hsl(var(--primary))]/20 text-[hsl(var(--primary))] font-medium">Admin</span>
              )}
            </Link>
          );
        })}
      </nav>

      {/* Bottom Section */}
      <div className="border-t border-white/10 p-3 space-y-3">
        {/* Credits */}
        {!collapsed && (
          <div className="flex items-center gap-2 px-3 py-2 rounded-lg bg-[hsl(var(--primary))]/10 border border-[hsl(var(--primary))]/20">
            <Coins size={16} className="text-[hsl(var(--primary))]" />
            <span className="text-sm tabular-nums font-medium">{user?.credits || 0}</span>
            <span className="text-xs text-[hsl(var(--muted-foreground))]">{t('common.credits')}</span>
          </div>
        )}
        
        {/* User */}
        <div className="flex items-center gap-2 px-3">
          <div className="w-7 h-7 rounded-full bg-[hsl(var(--secondary))] flex items-center justify-center text-xs font-medium">
            {user?.name?.charAt(0)?.toUpperCase() || 'U'}
          </div>
          {!collapsed && (
            <div className="flex-1 min-w-0">
              <p className="text-sm font-medium truncate">{user?.name}</p>
              <p className="text-xs text-[hsl(var(--muted-foreground))] truncate">{user?.email}</p>
            </div>
          )}
        </div>

        <button
          onClick={handleLogout}
          data-testid="nav-logout"
          className="flex items-center gap-2 px-3 py-2 w-full rounded-lg text-sm text-[hsl(var(--muted-foreground))] hover:text-red-400 hover:bg-red-500/10 transition-colors duration-200"
        >
          <LogOut size={16} />
          {!collapsed && <span>{t('nav.logout')}</span>}
        </button>
      </div>
    </div>
  );

  return (
    <div className="flex h-screen overflow-hidden bg-[hsl(var(--background))]">
      {/* Desktop Sidebar */}
      <aside className={`hidden lg:flex flex-col border-r border-white/10 bg-[hsl(var(--card))] transition-all duration-300 ${collapsed ? 'w-16' : 'w-60'}`}>
        <NavContent />
        <button
          onClick={() => setCollapsed(!collapsed)}
          className="p-2 m-2 rounded-lg hover:bg-white/5 text-[hsl(var(--muted-foreground))] transition-colors"
        >
          {collapsed ? <ChevronRight size={16} /> : <ChevronLeft size={16} />}
        </button>
      </aside>

      {/* Main Content */}
      <div className="flex-1 flex flex-col min-w-0">
        {/* Top Header */}
        <header className="h-14 border-b border-white/10 flex items-center justify-between px-4 bg-[hsl(var(--card))]/80 backdrop-blur">
          <div className="flex items-center gap-3">
            {/* Mobile menu */}
            <Sheet open={mobileOpen} onOpenChange={setMobileOpen}>
              <SheetTrigger asChild className="lg:hidden">
                <Button variant="ghost" size="icon"><Menu size={20} /></Button>
              </SheetTrigger>
              <SheetContent side="left" className="w-60 p-0 bg-[hsl(var(--card))] border-white/10">
                <NavContent />
              </SheetContent>
            </Sheet>
          </div>

          <div className="flex items-center gap-3">
            {/* Language Switcher */}
            <Select value={i18n.language?.split('-')[0] || 'tr'} onValueChange={(val) => i18n.changeLanguage(val)}>
              <SelectTrigger className="w-[120px] h-8 bg-transparent border-white/10 text-sm" data-testid="language-switcher">
                <SelectValue />
              </SelectTrigger>
              <SelectContent className="bg-[hsl(var(--popover))] border-white/10">
                {LANGUAGES.map(lang => (
                  <SelectItem key={lang.code} value={lang.code}>
                    <span className="flex items-center gap-2">
                      <span>{lang.flag}</span>
                      <span>{lang.label}</span>
                    </span>
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>

            {/* Credits */}
            <div className="hidden sm:flex items-center gap-1.5 px-3 py-1.5 rounded-full bg-[hsl(var(--primary))]/10 border border-[hsl(var(--primary))]/20">
              <Coins size={14} className="text-[hsl(var(--primary))]" />
              <span className="text-sm tabular-nums font-medium" data-testid="header-credits">{user?.credits || 0}</span>
            </div>

            {/* Admin View Badge */}
            <AdminViewBadge />
          </div>
        </header>

        {/* Page Content */}
        <main className="flex-1 overflow-auto p-4 sm:p-6 lg:p-8">
          {children}
        </main>
      </div>
    </div>
  );
}
